import { pageTypes } from 'config/pageTypes';
import sgpHero from 'assets/images/sgp/hero/sgp-general-hero-optimized.webp';
import AltContentImage from 'assets/images/sgp/5x10/alt-content-image.webp';
import StorageUnit5x10 from 'assets/images/sgp/5x10/5x10-storage-unit.webp';

import Furniture from 'assets/images/sgp/5x10/furniture.svg';
import Seasonal from 'assets/images/sgp/5x10/seasonal.svg';
import Moving from 'assets/images/sgp/5x10/moving.svg';
import SaveMoney from 'assets/images/sgp/5x10/save-money.svg';
import Electronics from 'assets/images/sgp/5x10/Media-and-electronics.svg';
import PersonalItem from 'assets/images/sgp/5x10/personal-item.svg';




import Organize from 'assets/images/sgp/5x10/organize.webp';
import Inventory from 'assets/images/sgp/5x10/inventory.webp';
import PackingBins from 'assets/images/sgp/5x10/packing-bins.webp';

import DemandIcon from 'assets/images/sgp/5x10/demand.svg';

import Home from 'assets/images/sgp/5x10/home.svg';
import CC from 'assets/images/sgp/5x10/CC.svg';
import Camera from 'assets/images/sgp/5x10/camera.svg';

import FeatureImage1 from 'assets/images/sgp/5x5/storage-unit-size.webp';
import FeatureImage2 from 'assets/images/sgp/5x5/how-storage-unit-measured.webp';
import FeatureImage3 from 'assets/images/sgp/5x5/5x5-storage.webp';
import FeatureImage4 from 'assets/images/sgp/5x5/50-things-fit-in-5x10.webp';


import threeColumnTableImage from 'assets/images/sgp/threeColumnTable.svg';
import fullWidthColumnTableImage from 'assets/images/sgp/fourColumnTable.svg';

const pageType = 'SGP';

const pspHeroContent = {
	pageType,
	title: 'Small Storage: 5x10 Storage Units Near You',
	description:
		"Your apartment feels like it's shrinking every time you bring home something new, and your closet is practically begging for mercy. You've mastered the art of Tetris with your belongings, but there's only so much you can stack before everything comes tumbling down. Sound familiar? Whether you're downsizing, decluttering, or just need breathing room, a 5x10 storage unit might be the space-saving hero you've been looking for.",
	image: sgpHero,
	CTAText: 'Search 5x10 Storage',
	inputLabel: 'Find Your Ideal 5x10 Storage Unit',
};

const alternateContentData1 = {
	title: 'Looking for a Small Storage Unit?',
	description:
		'If you’re here, chances are that you’re looking for a small storage unit to give your stuff a new home. Lucky for you, SpareFoot has a variety of storage unit sizes to suit your small storage needs. Here’s your Goldilocks storage unit menu to help you find the best storage space:',
	additionalProps: {
		paragraph: {
			color: 'black',
		},
	},
	content: [
		{
			title: '',
			image: AltContentImage,
			description:
				'**Medium Units:** 10x10, 10x15, 5x15\n\n' +
				'**Small Units:** 5x5, 5x10 (*you are here*), Mini storage, Storage lockers\n\n' +
				'**Large Units:** 10x20, 10x25, 10x30\n\n',
		},
	],
};

const videoImageSection = {
	content: {
		pageType,
		title: 'Benefits of a 5x10 Storage Unit Size',
		description:
			"So, how big is a 5x10 storage unit, and why should you care? Think of it as your decluttering sidekick – not too big, not too small, but just right for most people's storage needs. Here's why the 5x10 storage unit size hits the sweet spot.",
	},
	imageSrc: StorageUnit5x10,
};

const iconContentBlock1 = {
	headerContent: {
		prefix: '## Small Unit, Big Versatility',
		title: 'Will a 5x10 Storage Unit Work for Me?',
		description:
			'The 5x10 storage unit is a versatile storage option. Coming in at 50 square feet, this unit size is suited for a variety of needs.',
	},
	itemsContent: {
		items: [
			{
				icon: Furniture,
				title: '#### Perfect for Apartment Living',
				description:
					"Ideal for renters who need to store seasonal items, furniture between moves, or belongings that don't fit in cramped city apartments. It's like having an extra room without the extra rent.",
			},
			{
				icon: Seasonal,
				title: '#### Seasonal Storage Champion',
				description:
					'The 5x10 storage unit can handle your seasonal rotation without breaking a sweat. No more digging through your closet to find your winter coat in July.',
			},
			{
				icon: Moving,
				title: '#### Moving Made Easier',
				description:
					"Whether you're between apartments or storing furniture during a renovation, this size gives you flexibility without the commitment of a massive storage space you don't need.",
			},
			{
				icon: SaveMoney,
				title: '#### Budget-Friendly',
				description:
					"5x10 storage units offer the best value for most people's storage needs. You get enough space for your essentials without paying for square footage you won't use.",
			},
		],
	},
};

const alternateContentData2 = {
	title: '5x10 Storage Unit Size Guide & Comparison',
	description:
		"What does a 5x10 storage unit look like? Picture a walk-in closet that's about 5 feet wide and 10 feet deep – that's your 5x10 storage unit. With 50 square feet of space, it's compact but surprisingly roomy when you organize smart. Check out our pro tips below on how to make the most of your space.",
	additionalProps: {
		paragraph: {
			color: 'black',
		},
	},
	content: [
		{
			title: 'Vertical Storage is Your Friend',
			image: Organize,
			description:
				'Stack boxes and use shelving units to take advantage of the 8-foot ceiling height. Think up, not out.',
		},
		{
			title: 'Create Aisles',
			image: Inventory,
			description:
				'Leave a narrow pathway down the middle so you can access items stored in the back without having to move everything.',
		},
		{
			title: 'Use Clear Bins',
			image: PackingBins,
			description:
				"Label everything and use transparent containers so you can see what's inside without opening every box.",
		},
	],
};

const iconContentBlock2 = {
	headerContent: {
		prefix: '## A Versatile Storage Unit Size',
		title: 'What Can Fit in a 5x10 Storage Unit',
	},
	itemsContent: {
		items: [
			{
				icon: Furniture,
				title: '#### Furniture',
				description:
					'Small couch, dining table for 4, dresser, bookshelf, or several chairs',
			},
			{
				icon: Seasonal,
				title: '#### Appliances',
				description:
					'[Washer/dryer](https://www.sparefoot.com/blog/how-to-move-and-store-a-washer-and-dryer), refrigerator, or multiple small appliances',
			},
			{
				icon: Moving,
				title: '#### Seasonal Items',
				description:
					'Holiday decorations, winter/summer clothing, sports equipment',
			},
			{
				icon: SaveMoney,
				title: '#### Moving Boxes',
				description: '15-20 medium boxes plus some furniture',
			},
			{
				icon: Electronics,
				title: '#### Electronics',
				description: 'TV, gaming systems, computer equipment',
			},
			{
				icon: PersonalItem,
				title: '#### Personal Items',
				description: 'Books, clothes, photo albums, documents',
			},
		],
	},
};

const fullWidthTable = {
	isFullWidth: true,
	image: fullWidthColumnTableImage,
	title: 'Storage Size Comparison Quick Guide',
	description:
		'There are a lot of different storage unit options. Here’s a deeper look at how small and medium units compare, and what they’re best at storing.',
	slug: 'size-chart-section',
	columnWidths: [1, 3],
	pageType,
	headers: [
		{
			label: 'Unit Size',
			key: 'unitSize',
		},
		{
			label: 'Dimensions',
			key: 'dimensions',
		},
		{
			label: 'Square Footage',
			key: 'squareFootage',
		},
		{
			label: 'Use',
			key: 'use',
		},
	],
	rows: [
		{
			unitSize: '5x5',
			dimensions: "5' W x 5' L",
			squareFootage: '25 sq ft',
			use: {
				type: 'markdown',
				markdown: "**Best For:** A small closet's worth of items",
			},
		},
		{
			unitSize: '5x10',
			dimensions: "5' W x 10' L",
			squareFootage: {
				type: 'markdown',
				markdown: '**50 sq ft**',
			},
			use: {
				type: 'markdown',
				markdown: '**Best For:** 1-bedroom apartment items',
			},
		},
		{
			unitSize: '5x15',
			dimensions: "10' W x 10' L",
			squareFootage: '75 sq ft',
			use: {
				type: 'markdown',
				markdown: '**Best For:** Large bedroom plus storage',
			},
		},
		{
			unitSize: '10x10',
			dimensions: "10' W x 10' L",
			squareFootage: '100 sq ft',
			use: {
				type: 'markdown',
				markdown: '**Best For:** 2-bedroom apartment items',
			},
		},
	],
};

const ThreeColumnTable = {
	image: threeColumnTableImage,
	title: '5x10 Storage Unit Average Costs (2025)',
	description:
		"Let's talk numbers. 5x10 storage unit price varies based on location, features, and market demand, but here's what you can expect to pay in 2025:",
	slug: 'size-chart-section',
	columnWidths: [1, 3],
	pageType,
	headers: [
		{
			label: 'Storage Type',
			key: 'storageType',
		},
		{
			label: 'Average Monthly Cost',
			key: 'averageMonthlyCost',
		},
		{
			label: 'What You Get',
			key: 'whatYouGet',
		},
	],
	rows: [
		{
			storageType: {
				type: 'markdown',
				markdown: '**Basic Indoor**',
			},
			averageMonthlyCost: '$50-$120',
			whatYouGet: 'Standard 5x10 storage locker, basic security',
		},
		{
			storageType: {
				type: 'markdown',
				markdown: '**Climate-Controlled**',
			},
			averageMonthlyCost: '$80-$180',
			whatYouGet: 'Temperature control, humidity management',
		},
		{
			storageType: {
				type: 'markdown',
				markdown: '**Premium Indoor**',
			},
			averageMonthlyCost: '$100-$250',
			whatYouGet:
				'Enhanced security, better access hours, and newer facilities',
		},
		{
			storageType: {
				type: 'markdown',
				markdown: '**Outdoor/Drive-Up**',
			},
			averageMonthlyCost: '$40-$100',
			whatYouGet: 'Direct vehicle access, lower cost option',
		},
	],
};

const twoPanelBlock = {
	props: {
		title: 'Shop Smart for Your 5x10 Storage Unit',
		suffix: 'Understanding the factors that affect storage unit costs can help you find the right unit at the right price.',
		type: 'two-panel',
		content: [
			{
				image: DemandIcon,
				description:
					'### Demand \n\n Peak moving seasons (summer months) can drive prices up',
			},
			{
				image: SaveMoney,
				description:
					'### Promotions \n\n First-month free deals and long-term discounts can significantly reduce costs',
			},
		],
	},
};

const threePanelBlock = {
	props: {
		title: 'Factors Affecting 5x10 Storage Unit Price',
		type: 'three-panel',
		pageType: 'SGP',
		content: [
			{
				image: Home,
				description:
					'**Location, Location, Location** \n\n Urban areas typically cost more than suburban or rural locations',
			},
			{
				image: CC,
				description:
					'**Climate Control** \n\n Adds $20-$60 to your monthly cost, but protects sensitive items',
			},
			{
				image: Camera,
				description:
					'**Security Features** \n\n Facilities with better security systems command higher prices',
			},
		],
	},
};

const reviewsBlock = {
	pageType,
	title: 'Find Your Extra Stuff a 5x10 Storage Unit with SpareFoot',
	reviews: [
		{
			reviewTitle: 'SpareFoot Helped Me Save',
			reviewRating: 5,
			reviewText:
				'Super easy to navigate and compare rates to find the best deal. I locked in an awesome rate on a 5x10 unit during a one-day online-only special thanks to Sparefoot! Simple and helpful: like Expedia but for storage units!',
			reviewerInitials: 'CT',
			reviewerName: 'Caitlin Thomas',
			// reviewDate: 'Apr 9, 2021',
			link: 'https://www.trustpilot.com/reviews/606f7d2df85d750870510c2f',
		},
		{
			reviewTitle: 'Sparefoot makes getting a reservation…',
			reviewRating: 5,
			reviewText:
				"I was looking for a medium size reasonably priced storage unit near our home. I shopped around a little on the internet, made a few phone calls then I decided on renting a 5x10 unit at SpareFoot. The lady on the phone was very professional, friendly, and patiently answered all my questions, made recommendations and wasn't trying to sell me something I didn't want or need.",
			reviewerInitials: 'ZS',
			reviewerName: 'Zsuzsa S.',
			// reviewDate: 'Apr 21, 2015',
			link: 'https://www.trustpilot.com/reviews/553682730000ff0002df1953',
		},
		{
			reviewTitle: 'Quick and easy',
			reviewRating: 5,
			reviewText:
				'I was able to reserve what I needed online with SpareFoot, and the phone contact was well within 24 hours and very personable. Decent price compared to other storage places in the area. Convenient, quiet, and easy to get to. Highly recommend.',
			reviewerInitials: 'B',
			reviewerName: 'Barbara',
			// reviewDate: 'January 3rd, 2023',
			link: 'https://www.trustpilot.com/reviews/68d054bd80f72e95a81079be',
		},
		{
			reviewTitle: 'Great sort and filter tools to find lowest rates',
			reviewRating: 5,
			reviewText:
				'It took me only 3 minutes to find and reserve the lowest rate anywhere in SJ on a 5x10 storage unit. $75/month woo hoo!',
			reviewerInitials: 'CR',
			reviewerName: 'Chris R',
			// reviewDate: 'Feb 21, 2015',
			link: 'https://www.trustpilot.com/reviews/54e7d7a60000ff0002cecebe',
		},
	],
};

const faqBlock = {
	header: '5x10 Storage Unit Size FAQs',
	faqs: [
		{
			question: 'What can I fit in a 5x10 storage unit?',
			answer: 'A 5x10 storage unit can hold the contents of a small one-bedroom apartment, including furniture like a couch, dining table, dresser, and several boxes of personal items. Think of it as having about 50 square feet of storage space – roughly the size of a large walk-in closet. You can typically fit seasonal decorations, sports equipment, small appliances, and about 15-20 medium-sized moving boxes, along with some furniture pieces.',
		},
		{
			question: 'How much does a 5x10 storage unit cost per month?',
			answer:
				'5x10 storage unit pricing varies significantly based on location, facility features, and market demand. In 2025, you can expect to pay between $40-$250 per month for a 5x10 storage locker. Basic indoor units typically run $50-$120 monthly, while climate-controlled units range from $80-$180 per month. Premium facilities with enhanced security and amenities can cost $100-$250 monthly.\n\n' +
				'Location plays the biggest role in pricing – urban areas and coastal regions typically charge more than rural locations. Climate control adds about $20-$60 to your monthly cost but provides crucial protection for sensitive items like electronics, documents, and wooden furniture. Many facilities offer promotional rates, like a first-month free or discounted rates for annual commitments. Shopping around and comparing facilities can help you find cheap 5x10 storage units that fit your budget.',
		},
		{
			question: 'How big is a 5x10 storage unit exactly?',
			answer: "A 5x10 storage unit measures 5 feet wide by 10 feet long, giving you 50 square feet of floor space. The ceiling height is typically 8 feet, providing 400 cubic feet of total storage volume. To put this in perspective, it's about the size of a large walk-in closet or a small bedroom. When you walk into the unit, you'll have a narrow but deep space that requires smart organization to maximize storage capacity.",
		},
		{
			question: 'Do I need climate control for my 5x10 storage unit?',
			answer: "Whether you need climate control for your 5x10 storage space depends on what you're storing and your local climate conditions. Climate-controlled storage maintains temperature and humidity levels, protecting sensitive items from extreme heat, cold, and moisture damage. If you're storing electronics, important documents, wooden furniture, artwork, musical instruments, or vintage items, climate control is highly recommended.",
		},
		{
			question: 'Can I access my 5x10 storage unit anytime?',
			answer: "Access hours for your 5x10 storage locker depend on the specific facility you choose. Many modern storage facilities offer 24/7 access, allowing you to retrieve or store items whenever it's convenient for you. This is particularly popular with younger renters who may need access outside traditional business hours due to work schedules or lifestyle preferences.",
		},
		{
			question: 'What should I avoid storing in a 5x10 storage unit?',
			answer: 'Never store hazardous materials like gasoline, propane, paint, chemicals, or anything flammable. Perishable food items, plants, and living things are also prohibited as they can attract pests and create unsanitary conditions. Most facilities prohibit storing valuable items like cash, jewelry, important legal documents, or irreplaceable personal items like family heirlooms – these should be kept in a safety deposit box or at home. Stolen goods, illegal items, and anything with strong odors are also not allowed. Some facilities have specific restrictions on storing vehicles, large appliances, or business inventory, so always check with your facility about their storage policies before moving items into your 5x10 storage space.',
		},
	],
};

const featuredContent = {
	pageType,
	title: 'Learn More About Storage Unit Size Options',
	contentCards: [
		{
			title: 'General Storage Advice',
			date: 'August 7, 2025',
			image: FeatureImage1,
			content: 'How to Pick the Right Size Self-Storage Unit',
			link: 'https://www.sparefoot.com/blog/how-to-pick-the-right-size-storage-unit',
		},
		{
			title: 'General Storage Advice',
			date: 'October 17, 2025',
			image: FeatureImage2,
			content: 'How Are Storage Units Measured?',
			link: 'https://www.sparefoot.com/blog/how-are-storage-units-measured',
		},
		{
			title: 'General Storage Advice',
			date: 'October 17, 2025',
			image: FeatureImage3,
			content: 'What Can You Fit Inside a 5x5 Storage Unit?',
			link: 'https://www.sparefoot.com/blog/5x5-storage-unit-size-guide',
		},
		{
			title: 'Self Storage',
			date: 'October 29, 2025',
			image: FeatureImage4,
			content: '50 Things That Fit Inside a 5x10 Storage Unit',
			link: 'https://www.sparefoot.com/blog/50-things-you-can-store-in-5x10-storage-unit',
		},
	],
};

export const content5x10 = {
	pageType: pageTypes.SGP,
	type: '5x10',
	sections: [
		{
			title: 'Hero',
			background: 'yellow',
			modules: [
				{
					type: 'hero',
					id: '5x10Hero',
					background: 'lighterYellow',
					props: {
						content: pspHeroContent,
					},
				},
			],
		},
		{
			title: 'nearby units',
			background: 'gray',
			noPaddingBottom: true,
			modules: [
				{
					type: 'facilitiesNearYou',
					props: {
						title: 'Popular Facilities Near',
					},
				},
			],
		},
		{
			title: 'Alternate Content Section 1',
			modules: [
				{
					type: 'alternatingContent',
					id: 'AlternatContent1',
					props: {
						props: alternateContentData1,
					},
				},
			],
		},
		{
			title: 'Video Image Section',
			background: 'dark',
			modules: [
				{
					type: 'videoSection',
					id: '5x10VideoImageSection',
					props: videoImageSection,
				},
			],
		},
		{
			title: 'Icon Content Block 1',
			background: 'gray',
			modules: [
				{
					type: 'IconContentBlock',
					id: '5x10IconContentBlock1',
					props: iconContentBlock1,
				},
			],
		},
		{
			title: 'Alternate Content Section 2',
			modules: [
				{
					type: 'alternatingContent',
					id: 'alternatContent2',
					props: {
						props: alternateContentData2,
					},
				},
			],
		},
		{
			title: 'Icon Content Block 2',
			background: 'gray',
			modules: [
				{
					type: 'IconContentBlock',
					id: '5x10IconContentBlock2',
					props: iconContentBlock2,
				},
			],
		},
		{
			title: 'Full Width Table',
			modules: [
				{
					type: 'sizeChart',
					id: 'fullWidthTable',
					props: fullWidthTable,
				},
			],
		},
		{
			title: 'Three Column Table',
			background: 'gray',
			modules: [
				{
					type: 'sizeChart',
					id: 'ThreeColumnTable',
					props: ThreeColumnTable,
				},
			],
		},
		{
			title: 'Two Panel Block',
			modules: [
				{
					type: 'cardBlockContent',
					id: 'twoPanelBlock',
					props: twoPanelBlock,
				},
			],
		},
		{
			title: 'Three Panel Block',
			background: 'gray',
			modules: [
				{
					type: 'cardBlockContent',
					id: 'threePanelBlock',
					props: threePanelBlock,
				},
				{
					type: 'content',
					props: {
						contentType: 'row-type',
						ShowBackgroundImage: false,
						content: [
							'*Pro Tip: Cheap 5x10 storage units are often found at newer facilities trying to fill space or during off-peak seasons.*',
						],
					},
				},
			],
		},
		{
			title: 'Reviews Section',
			background: 'dark',
			modules: [
				{
					type: 'reviews',
					props: reviewsBlock,
				},
			],
		},
		{
			title: 'Faqs Section',
			modules: [
				{
					type: 'faqs',
					props: faqBlock,
				},
			],
		},
		{
			title: 'featured content',
			background: 'gray',
			noPaddingBottom: true,
			modules: [
				{
					type: 'featuredContent',
					props: featuredContent,
				},
			],
		},
		{
			title: 'Link to other SGPs',
			modules: [
				{
					type: 'links',
					props: {
						pageType,
						title: 'More Ways to Store',
						cards: ['ClimateControl', 'College', '5x5', '10x20'],
					},
				},
			],
		},
		{
			title: 'nearby units',
			noPaddingBottom: true,
			background: 'gray',
			modules: [
				{
					type: 'storageNearYou',
				},
			],
		},
	],
};
